import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, Bell, User } from 'lucide-react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import { notificationService } from '../../services/api/notifications';

interface DashboardLayoutProps {
  children?: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true); // Sidebar ouverte par défaut sur desktop
  const [isDesktop, setIsDesktop] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  // Gérer l'état du sidebar en fonction de la taille d'écran
  useEffect(() => {
    const handleResize = () => {
      const nextIsDesktop = window.innerWidth >= 1024; // lg breakpoint
      setIsDesktop(nextIsDesktop);
      if (nextIsDesktop) {
        setSidebarOpen(true); // Toujours ouvert sur desktop
      } else {
        setSidebarOpen(false); // Fermé par défaut sur mobile
      }
    };

    // Appeler une fois au montage pour définir l'état initial
    handleResize();

    // Écouter les changements de taille
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const refreshUnreadCount = async () => {
      try {
        const count = await notificationService.getUnreadCount();
        if (isMounted) {
          setUnreadCount(count);
        }
      } catch (error) {
        // Ne pas bloquer l'UI si l'API notifications est indisponible
        if (isMounted) {
          setUnreadCount(0);
        }
      }
    };

    refreshUnreadCount();
    const intervalId = window.setInterval(refreshUnreadCount, 60000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!isDesktop) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isDesktop]);


  const user = JSON.parse(localStorage.getItem('user') || '{"name": "Utilisateur", "email": ""}');

  return (
    <div className="min-h-screen">
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        isDesktop={isDesktop}
      />
      
      <div className="lg:ml-64 flex flex-col min-h-screen">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-40 lg:left-64">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <Menu className="w-5 h-5" />
              </button>
              
            </div>

            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <Link
                to="/workspace/notifications"
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg relative"
                aria-label="Ouvrir les notifications"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>

              {/* Profil utilisateur */}
              <div className="flex items-center space-x-3">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-gray-900">{user.name}</p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>
                <Link
                  to="/workspace/profile"
                  className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center hover:bg-primary-200 transition-colors"
                  aria-label="Ouvrir le profil"
                >
                  <User className="w-4 h-4 text-primary-600" />
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Contenu principal */}
        <main className="flex-1 relative z-0 px-3 sm:px-4 lg:px-6 pt-16 pb-6 flex flex-col">
          <motion.div
            className="relative z-10 flex-1"
            initial={false}
          >
            {children ?? <Outlet />}
          </motion.div>

          <footer
            className="mt-6 rounded-xl border border-gray-200 bg-white px-4 py-4 shadow-sm sm:px-6"
            aria-label="Pied de page"
          >
            <div className="flex flex-col gap-4 text-sm text-gray-700 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3">
                <span>
                  En partenariat avec <strong>GAVI</strong>
                </span>
                <img
                  src="/Gavi-logo_1b.png"
                  alt="Logo GAVI"
                  className="h-10 w-auto object-contain"
                  loading="eager"
                  decoding="async"
                />
              </div>

              <div className="flex items-center gap-3">
                <img
                  src="/logo-MSHP.png"
                  alt="Logo MSHP"
                  className="h-10 w-auto object-contain"
                  loading="eager"
                  decoding="async"
                />
              </div>

              <div className="flex items-center gap-3">
                <span>
                  Réalisé par <strong>efficience globale</strong>
                </span>
                <img
                  src="/logo-eg.png"
                  alt="Logo efficience globale"
                  className="h-10 w-auto object-contain"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
